import React, { useState, useMemo, useEffect, useRef } from "react";
import { formatInTimeZone } from "date-fns-tz";
import { addDays, startOfWeek } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { EventDetailsModal } from "./EventDetailsModal";
import { useSettings } from "@/providers/SettingsProvider";
import { TimeColumn } from "./TimeColumn";
import { DayColumn } from "./DayColumn";
import { WeekHeader } from "./WeekHeader";
import { useProcessedEvents } from "./hooks/useProcessedEvents";
import { EventObject } from "./types";
import { useRelatedEntities } from "@/hooks/useRelatedEntities";

interface WeekTimeGridProps {
	currentDate: Date;
	onTimeSlotClick: (hour: number, day: Date) => void;
	events: EventObject[];
	onEventUpdate?: (id: string, updatedEvent: { title: string; start: Date; end: Date; description?: string }) => void;
	onEventDelete?: (id: string) => void;
}

export const WeekTimeGrid = ({ 
	currentDate, 
	onTimeSlotClick, 
	events,
	onEventUpdate,
	onEventDelete
}: WeekTimeGridProps) => {
	const { calendarSettings, timezone } = useSettings();
	const userTimezone = timezone || 'UTC';
	
	const [selectedEvent, setSelectedEvent] = useState<EventObject | null>(null);
	const [showDetailsModal, setShowDetailsModal] = useState(false);
	// Track if we're in the process of deleting an event
	const isDeletingRef = useRef(false);
	
	const startHour = calendarSettings?.workDayStart || 9;
	const endHour = calendarSettings?.workDayEnd || 17;
	
	// Clear selected event when events change (e.g., after deletion or update)
	useEffect(() => {
		if (selectedEvent) {
			// Check if the selected event still exists in the events array
			const eventExists = events.some(e => e.id === selectedEvent.id);
			if (!eventExists) {
				// Cleanup when the event no longer exists
				setSelectedEvent(null);
				setShowDetailsModal(false);
			}
		}
	}, [events, selectedEvent]);
	
	// Reset the deleting flag after a short delay
	useEffect(() => {
		if (isDeletingRef.current) {
			const timer = setTimeout(() => {
				isDeletingRef.current = false;
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [isDeletingRef.current]);
	
	const timeSlots = useMemo(() => Array.from(
		{ length: (endHour - startHour + 1) * 2 },
		(_, i) => ({
			hour: startHour + Math.floor(i / 2),
			minutes: i % 2 === 0 ? 0 : 30
		})
	), [startHour, endHour]);
	
	const formatTimeLabel = (hour: number, minutes: number) => {
		return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
	};
	
	const weekDays = useMemo(() => {
		const firstDayOfWeek = calendarSettings?.firstDayOfWeek || 1;
		const start = startOfWeek(currentDate, { weekStartsOn: firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
		
		const allDays = Array.from({ length: 7 }, (_, i) => addDays(start, i));
		
		if (calendarSettings?.workWeekDays && calendarSettings.workWeekDays.length > 0) {
			return allDays.filter((_, index) => {
				const dayNumber = ((index + firstDayOfWeek) % 7).toString();
				return calendarSettings.workWeekDays.includes(dayNumber);
			});
		}
		
		return allDays.filter((_, index) => index < 5);
	}, [currentDate, calendarSettings]);
	
	const formatDayLabel = (date: Date) => {
		try {
			const dayName = formatInTimeZone(date, userTimezone, 'EEEE');
			const dayNumber = formatInTimeZone(date, userTimezone, 'd');
			return { dayName, dayNumber };
		} catch (error) {
			console.error("Error formatting day label:", error);
			return { dayName: '', dayNumber: '' };
		}
	};
	
	const processedEventsByDay = useProcessedEvents(events, weekDays, startHour, endHour);
	
	// Gather all unique candidate IDs for visible events this week
	const allCandidateIds = useMemo(() => {
		const ids = new Set<string>();
		Object.values(processedEventsByDay).forEach(dayEvents => {
			dayEvents.forEach(event => {
				if (event.metadata && Array.isArray(event.metadata.candidates)) {
					event.metadata.candidates.forEach(id => ids.add(id));
				}
			});
		});
		return Array.from(ids);
	}, [processedEventsByDay]);
	
	// Fetch candidate names
	const { entities: relatedEntities } = useRelatedEntities({
		candidates: allCandidateIds,
		offers: [],
		companies: [],
		matches: [],
		contacts: []
	});
	// Build a map of candidateId -> name
	const candidateNameMap = useMemo(() => {
		const map: Record<string, string> = {};
		relatedEntities.candidates.forEach(c => { map[c.id] = c.name; });
		return map;
	}, [relatedEntities.candidates]);
	
	const calculateEventStyle = (event: EventObject & { position: number }) => {
		try {
			const eventStart = new Date(event.start);
			const eventEnd = new Date(event.end);
			
			const startHourDecimal = eventStart.getHours() + eventStart.getMinutes() / 60;
			const endHourDecimal = eventEnd.getHours() + eventEnd.getMinutes() / 60;
			
			const visibleStartHour = Math.max(startHourDecimal, startHour);
			const visibleEndHour = Math.min(endHourDecimal, endHour + 1);
			
			if (visibleEndHour <= visibleStartHour) {
				return { display: 'none' };
			}
			
			const topOffset = (visibleStartHour - startHour) * 4;
			const height = Math.max((visibleEndHour - visibleStartHour) * 4, 1);
			
			const maxPositions = event.position + 1;
			const width = `calc((100% - 8px) / ${maxPositions})`;
			const left = `calc(${(event.position / maxPositions) * 100}% + 4px)`;
			
			return {
				top: `${topOffset}rem`,
				height: `${height}rem`,
				width,
				left,
				backgroundColor: event.color || calendarSettings?.defaultEventColor || '#9b87f5',
				zIndex: 10 + event.position
			};
		} catch (error) {
			console.error("Error calculating event style:", error);
			return { display: 'none' };
		}
	};
	
	const handleEventClick = (event: EventObject) => {
		// Avoid selecting an event if we're in the process of deleting one
		if (isDeletingRef.current) return;
		
		setSelectedEvent(event);
		setShowDetailsModal(true);
	};
	
	const handleCloseModal = () => {
		setShowDetailsModal(false);
		// Clear the selected event after modal is closed
		setTimeout(() => {
			setSelectedEvent(null);
		}, 200);
	};
	
	const handleTimeSlotClick = (hour: number, minutes: number) => {
		// Avoid time slot clicks if we're in the process of deleting
		if (isDeletingRef.current) return;
		onTimeSlotClick(hour, new Date());
	};
	
	const handleEventDelete = (id: string) => {
		if (onEventDelete) {
			// Set the deleting flag to prevent immediate interactions
			isDeletingRef.current = true;
			
			// Clear the selected event state first
			setSelectedEvent(null);
			
			// Then call the passed delete function
			onEventDelete(id);
		}
	};
	
	return (
		<>
			<div className="calendar-container rounded-xl overflow-hidden bg-white/60 dark:bg-black/40 backdrop-blur-md border border-white/20 dark:border-white/10 shadow-sm">
				<WeekHeader weekDays={weekDays} formatDayLabel={formatDayLabel} />
				
				<ScrollArea className="h-[calc(100vh-16rem)]">
					<div className="grid relative" style={{ 
						gridTemplateColumns: `80px repeat(${weekDays.length}, 1fr)`
					}}>
						<div className="col-span-full" style={{ height: '1rem' }} />
						<TimeColumn 
							timeSlots={timeSlots}
							formatTimeLabel={formatTimeLabel}
						/>
						
						{weekDays.map((day, dayIndex) => {
							const dateKey = day.toISOString().split('T')[0];
							const dayEvents = processedEventsByDay[dateKey] || [];
							
							return (
								<DayColumn
									key={dayIndex}
									timeSlots={timeSlots}
									events={dayEvents}
									onTimeSlotClick={(hour, minutes) => onTimeSlotClick(hour, day)}
									calculateEventStyle={calculateEventStyle}
									onEventClick={handleEventClick}
									userTimezone={userTimezone}
									candidateNameMap={candidateNameMap}
								/>
							);
						})}
					</div>
				</ScrollArea>
			</div>
			
			{selectedEvent && (
				<EventDetailsModal 
					event={selectedEvent}
					isOpen={showDetailsModal}
					onClose={handleCloseModal}
					onUpdate={onEventUpdate}
					onDelete={handleEventDelete}
				/>
			)}
		</>
	);
};
